/* ============================================================================
 * ELIA — PaywallTeaser (P1 · capability-driven soft conversion)
 * ----------------------------------------------------------------------------
 * Doctrine (2026-02-09 · Founder):
 *  "Le paywall ne doit pas être construit uniquement autour des plans Stripe.
 *   Il doit être piloté par le moteur d'entitlements / capabilities."
 *
 * Behaviour:
 *  - Reads the exact capability decision (allowed / missing / reason_fr)
 *    from the backend resolver — never guesses which subscription is needed.
 *  - Explains WHICH capability is blocked, WHY (missing role, license,
 *    module, entitlement, admin revoke…), and HOW to unblock it (upgrade,
 *    contact your advisor, wait for admin approval…).
 *  - Renders a blurred preview of the underlying content + a green checklist
 *    of what will be unlocked — never a hard wall.
 * ==========================================================================*/
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Lock, ArrowRight, ShieldCheck, Check, X, Info } from "lucide-react";
import { useCap } from "@/lib/capabilities";

/**
 * Human-readable label for each "missing" gate — matches the backend
 * capabilities resolver (core/capabilities.py). Order matters for UX.
 */
const MISSING_LABELS = {
  role: {
    icon: Lock,
    title: "Réservé à un autre profil",
    hint: "Cette action est exclusive à certains rôles (professionnel habilité, institution ou administrateur). Contactez votre conseiller ALTIORA si vous pensez que c'est une erreur.",
    accent: "#5C1A5C",
  },
  license: {
    icon: ShieldCheck,
    title: "Habilitation réglementaire requise",
    hint: "Cette action exige une licence professionnelle vérifiée (courtier hypothécaire, notaire, CPA, fiscaliste…). Elle protège juridiquement le bénéficiaire de votre conseil.",
    accent: "#7A5B14",
  },
  module: {
    icon: Sparkles,
    title: "Module à activer sur ce dossier",
    hint: "Ce moteur ALTIORA n'est pas encore actif sur votre dossier. Ouvrez-le depuis la vue Modules pour lancer la première analyse.",
    accent: "#065F46",
  },
  client_type: {
    icon: Info,
    title: "Type de dossier incompatible",
    hint: "Cette fonctionnalité s'applique à un autre type de client (personne physique vs organisation). Ouvrez le dossier approprié pour continuer.",
    accent: "#0B2545",
  },
  entitlement: {
    icon: Sparkles,
    title: "Débloquez cette fonctionnalité",
    hint: "Elle est incluse dans un plan supérieur ou obtenable via une licence institutionnelle, une habilitation, ou un accès accordé par un administrateur.",
    accent: "#0B2545",
  },
  revoke: {
    icon: X,
    title: "Accès révoqué",
    hint: "Un administrateur a désactivé cet accès pour votre compte. Contactez le support ALTIORA pour comprendre la raison et les recours possibles.",
    accent: "#B91C1C",
  },
};

/**
 * PaywallTeaser · capability-driven.
 *
 * Props:
 *   - capability (str, REQUIRED)  key from the capability registry
 *   - unlockedByPlans ([str])     which ELIA plans unlock it (for the CTA copy)
 *   - benefits ([str])            optional custom benefit list (falls back to a generic set)
 *   - preview (ReactNode)         optional blurred preview of the underlying content
 *   - compact (bool)              omits benefits list in tight contexts
 *
 * If you already know the decision, use PaywallFromDecision instead.
 */
export default function PaywallTeaser({
  capability,
  unlockedByPlans = ["ALTIORA Plus", "ALTIORA Business Pro", "ALTIORA Institution"],
  benefits,
  preview = null,
  compact = false,
}) {
  const { allowed, reason_fr, missing, loading } = useCap(capability);
  if (loading) return null;
  if (allowed) return null;
  return (
    <PaywallFromDecision
      reason={reason_fr}
      missing={missing}
      unlockedByPlans={unlockedByPlans}
      benefits={benefits}
      preview={preview}
      compact={compact}
      capabilityKey={capability}
    />
  );
}

export function PaywallFromDecision({
  reason,
  missing = [],
  unlockedByPlans = [],
  benefits,
  preview = null,
  compact = false,
  capabilityKey = "",
}) {
  // Determine the primary missing gate to shape the CTA
  const primary = ["revoke", "license", "role", "module", "client_type", "entitlement"].find(
    (k) => (missing || []).includes(k),
  ) || "entitlement";
  const meta = MISSING_LABELS[primary];
  const Icon = meta.icon;

  const defaultBenefits = [
    "Accès aux moteurs ALTIORA avancés",
    "Rapports PDF premium — méthodologie IMES™ v2",
    "IA Claude Sonnet · narration personnalisée",
    "Coffre documentaire avancé (10 Go)",
    "Simulations et prévisions illimitées",
    "Support prioritaire",
  ];
  const finalBenefits = benefits && benefits.length ? benefits : defaultBenefits;

  const canUpgrade = primary === "entitlement";
  const canContact = ["license", "role", "revoke", "module"].includes(primary);

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0, transition: { duration: 0.5 } }}
      className="rounded-xl border-2 relative overflow-hidden"
      style={{ borderColor: meta.accent, backgroundColor: `${meta.accent}08` }}
      data-testid={`paywall-teaser${capabilityKey ? `-${capabilityKey}` : ""}`}
    >
      {/* Blurred preview background */}
      {preview && (
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{ filter: "blur(8px) saturate(0.7)" }}
          aria-hidden
        >
          {preview}
        </div>
      )}

      <div className="relative p-6 md:p-10">
        <div className="inline-flex items-center gap-2 mb-4">
          <div
            className="h-8 w-8 rounded-md flex items-center justify-center"
            style={{ backgroundColor: `${meta.accent}15`, color: meta.accent }}
          >
            <Icon className="h-4 w-4" strokeWidth={2} />
          </div>
          <span className="text-[10px] font-mono uppercase tracking-[0.28em]" style={{ color: meta.accent }}>
            {meta.title}
          </span>
        </div>

        <h2 className="font-display text-2xl md:text-3xl text-[#1F2937] tracking-tight leading-tight mb-3">
          {reason && reason.split(".")[0]}.
        </h2>
        <p className="text-sm md:text-base text-[#5B5B62] leading-relaxed max-w-2xl mb-2">
          {meta.hint}
        </p>
        {reason && reason.split(".").length > 1 && (
          <p className="text-xs text-[#7A5B14] leading-relaxed max-w-2xl mb-6">
            {reason}
          </p>
        )}

        {/* Benefits — green checklist */}
        {!compact && canUpgrade && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-2.5" data-testid="paywall-benefits">
            {finalBenefits.map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0, transition: { delay: 0.04 * i } }}
                className="flex items-start gap-2"
              >
                <div className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="h-3 w-3" strokeWidth={2.5} />
                </div>
                <div className="text-sm text-[#1F2937] leading-snug">{b}</div>
              </motion.div>
            ))}
          </div>
        )}

        {/* CTAs — adapt to the missing gate */}
        <div className="mt-8 flex items-center gap-3 flex-wrap">
          {canUpgrade && (
            <Link
              to="/#pricing"
              data-testid="paywall-cta-plans"
              className="inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#123163] text-white text-sm font-medium px-5 py-2.5 transition-all"
            >
              Voir les plans <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </Link>
          )}
          {canContact && (
            <a
              href="mailto:support@altioracoop.com"
              data-testid="paywall-cta-contact-support"
              className="inline-flex items-center gap-2 rounded-md border border-[#E4E1D8] hover:border-[#0B2545] text-[#1F2937] text-sm font-medium px-5 py-2.5 transition-all"
            >
              Contacter le support
            </a>
          )}
          {canUpgrade && unlockedByPlans.length > 0 && (
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
              Débloqué avec : {unlockedByPlans.join(" · ")}
            </span>
          )}
        </div>

        {/* Doctrinal reassurance */}
        <div className="mt-8 pt-5 border-t border-[#E4E1D8]/60 flex items-start gap-2.5">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
          <p className="text-[11px] text-[#8B8B8F] leading-relaxed max-w-2xl">
            ALTIORA ne bloque jamais artificiellement. L&apos;accès à cette fonctionnalité
            peut aussi vous être ouvert par une licence institutionnelle, une habilitation
            professionnelle, ou une permission accordée par un administrateur.
          </p>
        </div>
      </div>
    </motion.section>
  );
}
